/** @file One-point rotation: the node's own bone on screen minus the drawn angle (design 21.3). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { projectOffset } from '@zilo/pose3d-render-core/camera/projectOffset';
import { screenAngle } from '@zilo/pose3d-render-core/camera/screenAngle';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { wrapSignedDegrees } from '@zilo/pose3d-render-core/camera/wrapSignedDegrees';
import type { NodePoints } from '@zilo/pose3d-render-core/place/nodePointsOf';

/**
 * `theta = wrap(screenAngle(N' - O') - d)` in [-180, 180) from the rounded node and bone origin; 0 for a root or a bone
 * shorter than 1e-9 px. `d` is the pick's drawn screen angle (already `360 - d` on a mirrored tile).
 */
export function screenRotationOf(points: Pick<NodePoints, 'n' | 'o' | 'root'>, view: View, drawnScreenAngleDeg: number): number {
  if (points.root) return 0;
  const bone: Vec3 = [points.n[0] - points.o[0], points.n[1] - points.o[1], points.n[2] - points.o[2]];
  const s = projectOffset(bone, view);
  return Math.hypot(s[0], s[1]) < 1e-9 ? 0 : wrapSignedDegrees(screenAngle(s[0], s[1]) - drawnScreenAngleDeg);
}
